export const convertQueryObjToString = (obj) => {
  if (!obj) return '';
  const keys = Object.keys(obj);
  return keys
    .map((key) =>
      obj[key]
        ? Array.isArray(obj[key])
          ? obj[key].map((item) => `${key}[]=${item}`).join('&')
          : `${key}=${obj[key]}`
        : '',
    )
    .filter((e) => !!e)
    .join('&');
};

const removeCommas = (filters) => {
  if (!filters) return '';
  const keys = Object.keys(filters);
  const params = keys
    ?.map((e) => {
      const valuesArray = filters[e] && filters[e] != 'null' ? filters[e]?.split(',') : '';
      return (
        valuesArray &&
        valuesArray
          ?.map((val) => {
            return `${e}=${val}`;
          })
          .join('&')
      );
    })
    .join('&');
  return params;
};

export const getQueryString = (filters, removeCommaSeperated = false) => {
  const queryString =
    typeof filters === 'string'
      ? filters
      : removeCommaSeperated
        ? removeCommas(filters)
        : convertQueryObjToString(filters);
  return queryString;
};

export const mapQueryStringToFilterObject = (search, cb = () => {}) => {
  const queryObj = {};
  const queryString = search?.split('?')[1];

  if (queryString) {
    const items = decodeURI(queryString)?.split('&');
    for (const item of items) {
      const key = item?.split('=')[0];
      const value = item?.split('=')[1];
      cb(key, value);
      if (key && value) {
        if (queryObj[key]) {
          queryObj[key] += `,${value}`;
        } else {
          queryObj[key] = value;
        }
      }
    }
  }

  return { queryObj };
};

/**
 * A filter's `queryParamKey` describes where its value belongs in a nested request body:
 *
 *   f[location_id][]    -> f.location_id = [...]   (`[]` marks an array)
 *   f[created_at][gte]  -> f.created_at.gte = ...  (a trailing segment nests)
 *   f[lead_client.id]   -> f['lead_client.id']     (a dotted segment stays one key)
 *
 * These helpers turn such a key, plus the value the URL carries for it, into that shape. Callers
 * keep their own policy — which roots to coerce, how to spread a range across two keys — and use
 * these for the mechanical part, so a second hand-written key list never has to exist.
 */
const QUERY_PARAM_KEY_PATTERN = /^([a-z]+)((?:\[[^\]]*\])+)$/i;

/** `{ root, path, isArray }`, or null when the key is not in bracket form. */
export const parseQueryParamKey = (queryParamKey) => {
  const match = typeof queryParamKey === 'string' && queryParamKey.match(QUERY_PARAM_KEY_PATTERN);
  if (!match) return null;
  const [, root, bracketed] = match;
  const segments = bracketed.match(/\[[^\]]*\]/g).map((segment) => segment.slice(1, -1));
  const isArray = segments[segments.length - 1] === '';
  return { root, path: isArray ? segments.slice(0, -1) : segments, isArray };
};

/**
 * mapQueryStringToFilterObject decodes the query string with decodeURI, which by design leaves
 * reserved characters encoded — so a multi-value filter arrives as "a%2Cb", not "a,b", and would
 * never split. mapQueryParams decodes each value with decodeURIComponent for the same reason; this
 * mirrors it. Guarded, because a malformed percent sequence throws.
 */
export const decodeParamValue = (value) => {
  if (value === undefined || value === null) return value;
  try {
    return decodeURIComponent(`${value}`);
  } catch {
    return `${value}`;
  }
};

/** The URL carries everything as a string; a JSON body wants real numbers and booleans. */
export const coerceQueryParamValue = (value) => {
  if (value === 'true') return true;
  if (value === 'false') return false;
  if (value !== '' && !Number.isNaN(Number(value))) return Number(value);
  return value;
};

/** Writes `value` into `target` at `path`, creating the objects along the way. */
export const assignAtPath = (target, path, value) => {
  let cursor = target;
  path.slice(0, -1).forEach((segment) => {
    if (!cursor[segment] || typeof cursor[segment] !== 'object') cursor[segment] = {};
    cursor = cursor[segment];
  });
  cursor[path[path.length - 1]] = value;
};
